import { focusFormControl } from "@vahak/core-ui/dist/methods/keyboardViewport";

import { LoadQuoteValues } from "../../load-quote";
import { getQuoteFormCapacityError } from "./getQuoteFormCapacityError";
import { ROUTE_QUOTE_SKIP_CAPACITY_VALIDATION } from "./loadQuoteForm.constants";

export type QuoteFormFieldId = "quote-pickup-city" | "quote-drop-city" | "quote-tonnes";

export type QuoteFormErrors = Partial<Record<QuoteFormFieldId, string>>;

const QUOTE_FORM_FIELD_ORDER: QuoteFormFieldId[] = ["quote-pickup-city", "quote-drop-city", "quote-tonnes"];

export type QuoteFormValidationOptions = {
    isRemoteConfigLoaded?: boolean;
    t?: (key: string, opts?: object) => string;
    skipCapacityValidation?: boolean;
};

export function getQuoteFormErrors(values: LoadQuoteValues, options?: QuoteFormValidationOptions): QuoteFormErrors {
    const errors: QuoteFormErrors = {};
    const isRemoteConfigLoaded = options?.isRemoteConfigLoaded ?? true;
    const t = options?.t ?? ((key: string) => key);
    const skipCapacityValidation = options?.skipCapacityValidation ?? false;

    if (!values.source?.place_id) {
        errors["quote-pickup-city"] = "Please select pickup city";
    }
    if (!values.destination?.place_id) {
        errors["quote-drop-city"] = "Please select drop city";
    }

    if (!skipCapacityValidation) {
        const capacityError = getQuoteFormCapacityError(values.tonnes, isRemoteConfigLoaded, t);
        if (capacityError) {
            errors["quote-tonnes"] = capacityError;
        }
    }

    return errors;
}

export function isQuoteFormValid(values: LoadQuoteValues, options?: QuoteFormValidationOptions): boolean {
    return Object.keys(getQuoteFormErrors(values, options)).length === 0;
}

export function getFirstQuoteFormInvalidFieldId(
    values: LoadQuoteValues,
    options?: QuoteFormValidationOptions
): QuoteFormFieldId | undefined {
    const errors = getQuoteFormErrors(values, options);
    return QUOTE_FORM_FIELD_ORDER.find((fieldId) => errors[fieldId]);
}

export function getRouteQuoteFormValidationOptions(
    isRemoteConfigLoaded: boolean,
    t: NonNullable<QuoteFormValidationOptions["t"]>
): QuoteFormValidationOptions {
    return { isRemoteConfigLoaded, t, ...ROUTE_QUOTE_SKIP_CAPACITY_VALIDATION };
}

export type FocusQuoteFormFieldOptions = {
    fieldIdMap?: Partial<Record<QuoteFormFieldId, string>>;
    delay?: number;
    scrollIntoView?: boolean;
    /** Gap kept between the focused field and the keyboard. */
    clearance?: number;
};

function resolveFocusQuoteFormFieldOptions(
    fieldIdMapOrOptions?: Partial<Record<QuoteFormFieldId, string>> | FocusQuoteFormFieldOptions
): FocusQuoteFormFieldOptions {
    if (!fieldIdMapOrOptions) {
        return {};
    }

    const maybeFieldIdMap = fieldIdMapOrOptions as Partial<Record<QuoteFormFieldId, string>>;
    if (
        "quote-pickup-city" in maybeFieldIdMap ||
        "quote-drop-city" in maybeFieldIdMap ||
        "quote-tonnes" in maybeFieldIdMap
    ) {
        const hasFocusOptions =
            "delay" in fieldIdMapOrOptions ||
            "scrollIntoView" in fieldIdMapOrOptions ||
            "clearance" in fieldIdMapOrOptions ||
            "fieldIdMap" in fieldIdMapOrOptions;

        if (!hasFocusOptions) {
            return { fieldIdMap: maybeFieldIdMap };
        }
    }

    return fieldIdMapOrOptions as FocusQuoteFormFieldOptions;
}

function getQuoteFormFieldCandidateIds(
    fieldId: QuoteFormFieldId,
    fieldIdMap?: Partial<Record<QuoteFormFieldId, string>>
) {
    const resolvedId = fieldIdMap?.[fieldId] ?? fieldId;
    return [resolvedId, `${resolvedId}__input`, `${resolvedId}__container`];
}

export function focusQuoteFormField(
    fieldId: QuoteFormFieldId,
    fieldIdMapOrOptions?: Partial<Record<QuoteFormFieldId, string>> | FocusQuoteFormFieldOptions
) {
    const options = resolveFocusQuoteFormFieldOptions(fieldIdMapOrOptions);

    focusFormControl({
        candidateIds: getQuoteFormFieldCandidateIds(fieldId, options.fieldIdMap),
        delay: options.delay,
        scrollIntoView: options.scrollIntoView,
        clearance: options.clearance
    });
}
